/**
 * The Three.js adapter.
 *
 * The canonical source frame is right-handed and Z-up. Three.js is
 * right-handed and Y-up. The conversion happens here, once, for every
 * position the renderer sees:
 *
 *     (x, y, z)_canonical  ->  (x, z, -y)_three
 *
 * The determinant of that basis change is +1, so handedness is preserved and
 * the outward winding produced by `core/mesh.ts` stays outward. No other file
 * in the viewer is allowed to touch an axis.
 */

import * as THREE from 'three';
import type { Box, Solid, SolidRole } from 'levelspec';
import { boxMesh } from 'levelspec';

export function toThree(x: number, y: number, z: number): THREE.Vector3 {
  return new THREE.Vector3(x, z, -y);
}

/** Inverse conversion, for reporting the camera position in source units. */
export function fromThree(v: THREE.Vector3): [number, number, number] {
  return [v.x, -v.z, v.y];
}

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

export interface RoleStyle {
  color: number;
  label: string;
  opacity?: number;
  metalness?: number;
  roughness?: number;
}

export const ROLE_STYLE: Record<SolidRole, RoleStyle> = {
  floor: { color: 0x6d7278, label: 'Floor slab', roughness: 0.95 },
  roof: { color: 0x4c5157, label: 'Roof slab', roughness: 0.95 },
  static_hard: { color: 0x9aa2ab, label: 'Interior wall', roughness: 0.9 },
  exterior_wall: { color: 0x8a8378, label: 'Exterior wall', roughness: 0.92 },
  soft_panel: { color: 0xc99b5e, label: 'Soft wall (destructible)', roughness: 0.85 },
  reinforcement_slot: { color: 0x5b7fa6, label: 'Reinforcement slot', roughness: 0.55, metalness: 0.45 },
  glass: { color: 0x7fd4e0, label: 'Glass', opacity: 0.24, roughness: 0.1 },
  window_barricade: { color: 0xa8703c, label: 'Window barricade', roughness: 0.9 },
  door_panel: { color: 0x8f5a33, label: 'Door panel', roughness: 0.9 },
  lintel: { color: 0x8e959d, label: 'Lintel', roughness: 0.9 },
  sill: { color: 0x8e959d, label: 'Sill', roughness: 0.9 },
  stair: { color: 0x7f858c, label: 'Stair', roughness: 0.95 },
  ramp: { color: 0x777d85, label: 'Ramp', roughness: 0.95 },
  ladder: { color: 0xb4b9be, label: 'Ladder', roughness: 0.4, metalness: 0.6 },
  hatch: { color: 0xb07a44, label: 'Hatch (destructible)', roughness: 0.88 },
  cover: { color: 0x8a7d5e, label: 'Cover', roughness: 0.9 },
  riser: { color: 0x7a8089, label: 'Riser (sealed drop)', roughness: 0.93 },
};

/** Surfaces that can be removed at runtime — never fused into the shell. */
export const DYNAMIC_ROLES: SolidRole[] = ['soft_panel', 'hatch', 'window_barricade', 'door_panel', 'glass'];

export const MARKER_STYLE: Record<string, { color: number; label: string }> = {
  attacker_spawn: { color: 0xff6b5a, label: 'Attacker spawn' },
  defender_spawn: { color: 0x62a8ff, label: 'Defender spawn' },
  site: { color: 0xffd75e, label: 'Site' },
  objective: { color: 0xffb03a, label: 'Objective' },
  poi: { color: 0xa78bfa, label: 'Point of interest' },
  drone_hole: { color: 0x7de08a, label: 'Drone hole' },
};

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/**
 * Merge a set of solids into one geometry, converting the canonical mesh into
 * the Three.js frame. Triangle order is untouched; only the basis changes.
 *
 * `tint`, when given, writes a per-solid vertex colour. Merging by role is
 * what keeps the draw call count down, and the cost of that is a wall of one
 * flat colour where you cannot tell one box from the next; a small per-solid
 * shade puts the edges back without splitting the mesh.
 */
export function geometryForSolids(
  solids: Solid[],
  tint?: (s: Solid, index: number) => [number, number, number],
): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  let base = 0;

  for (const [si, s] of solids.entries()) {
    const rgb = tint?.(s, si);
    const m = boxMesh(s.box);
    for (let i = 0; i < m.positions.length; i += 3) {
      const [x, y, z] = [m.positions[i], m.positions[i + 1], m.positions[i + 2]];
      const [nx, ny, nz] = [m.normals[i], m.normals[i + 1], m.normals[i + 2]];
      positions.push(x, z, -y);
      normals.push(nx, nz, -ny);
      // Box mapping in canonical meters: one texture tile per metre on every
      // face, so the grid reads at the same scale wherever you stand.
      if (Math.abs(nx) > 0.5) uvs.push(y, z);
      else if (Math.abs(nz) > 0.5) uvs.push(x, y);
      else uvs.push(x, z);
      if (rgb) colors.push(rgb[0], rgb[1], rgb[2]);
    }
    for (const idx of m.indices) indices.push(base + idx);
    base += m.positions.length / 3;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  if (colors.length) geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geo.setIndex(indices);
  geo.computeBoundingSphere();
  return geo;
}

/** A one-metre grid, drawn once and shared by every material. */
let gridTexture: THREE.Texture | null = null;
function gridMap(): THREE.Texture {
  if (gridTexture) return gridTexture;
  const S = 128;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, S, S);
  ctx.strokeStyle = 'rgba(0,0,0,0.20)';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, S - 2, S - 2);
  ctx.strokeStyle = 'rgba(0,0,0,0.07)';
  ctx.lineWidth = 1;
  for (let i = 1; i < 4; i++) {
    const t = (i * S) / 4;
    ctx.beginPath();
    ctx.moveTo(t, 0);
    ctx.lineTo(t, S);
    ctx.moveTo(0, t);
    ctx.lineTo(S, t);
    ctx.stroke();
  }
  gridTexture = new THREE.CanvasTexture(c);
  gridTexture.wrapS = gridTexture.wrapT = THREE.RepeatWrapping;
  gridTexture.anisotropy = 8;
  gridTexture.colorSpace = THREE.SRGBColorSpace;
  return gridTexture;
}

export function materialForRole(role: SolidRole): THREE.Material {
  const style = ROLE_STYLE[role];
  const transparent = style.opacity !== undefined;
  return new THREE.MeshStandardMaterial({
    color: style.color,
    map: transparent ? null : gridMap(),
    roughness: style.roughness ?? 0.9,
    metalness: style.metalness ?? 0.02,
    transparent,
    opacity: style.opacity ?? 1,
    side: transparent ? THREE.DoubleSide : THREE.FrontSide,
    depthWrite: !transparent,
  });
}

export function boxToThreeBox3(b: Box): THREE.Box3 {
  return new THREE.Box3(
    new THREE.Vector3(b.min[0], b.min[2], -b.max[1]),
    new THREE.Vector3(b.max[0], b.max[2], -b.min[1]),
  );
}
